import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { useCallback, useState } from "react";
import type { RefObject } from "react";
import { Button, Input, Select, SelectItem } from "@heroui/react";

// Minimal interface for AntD TextArea component instance used for ref unwrapping
interface AntdTextAreaInstance {
  resizableTextArea?: {
    textArea?: HTMLTextAreaElement | null;
  };
  focus?: (options?: unknown) => void;
  blur?: () => void;
}

/**
 * Resolve the underlying input/textarea DOM element from a ref that may reference
 * a native element or an Ant Design TextArea component instance.
 *
 * @param ref - React ref that may point to an `HTMLInputElement`, `HTMLTextAreaElement`,
 *              or an Ant Design TextArea instance with `resizableTextArea.textArea`.
 * @returns The native input/textarea element if resolvable; otherwise `null`.
 */
function resolveDomElement(
  ref?: RefObject<
    HTMLInputElement | HTMLTextAreaElement | AntdTextAreaInstance
  >,
): HTMLInputElement | HTMLTextAreaElement | null {
  const current = ref?.current;
  if (!current) return null;
  if (
    current instanceof HTMLInputElement ||
    current instanceof HTMLTextAreaElement
  ) {
    return current;
  }
  const maybeAntd = current as AntdTextAreaInstance;
  return maybeAntd.resizableTextArea?.textArea ?? null;
}

interface FormValues {
  value: string | undefined;
  path: string | undefined;
}

/**
 *
 * @returns DynamicButtons component
 */
export default function DynamicButtons({
  disabled = false,
  dataPull = false,
  targetInputRef,
}: {
  disabled?: boolean;
  dataPull?: boolean;
  targetInputRef?: RefObject<
    HTMLInputElement | HTMLTextAreaElement | AntdTextAreaInstance
  >;
}) {
  const { t } = useTranslation();

  const { setFieldValue, values } = useFormikContext<FormValues>();
  const [period, setPeriod] = useState<string>("last");
  const [quantity, setQuantity] = useState<number>(1);
  const [frequency, setFrequency] = useState<string>("day");
  const [dateTimeFormat, setDateTimeFormat] = useState<string>("%Y%m%d.0");

  const updateDateTimeFormat = useCallback(
    (e) => {
      setDateTimeFormat(e.target.value);
    },
    [setDateTimeFormat],
  );

  const updateQuantity = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = parseInt(e.target.value, 10);
      if (!isNaN(val) && val >= 1 && val <= 100) {
        setQuantity(val);
      }
    },
    [setQuantity],
  );

  const updatePeriod = useCallback(
    (e) => {
      setPeriod(e.target.value);
    },
    [setPeriod],
  );

  const updateFrequency = useCallback(
    (e) => {
      setFrequency(e.target.value);
    },
    [setFrequency],
  );

  const addField = useCallback(() => {
    const injection = `{${period}${quantity}${frequency}:${dateTimeFormat}}`;

    // Determine current string based on mode
    const currentValue = dataPull ? (values.path ?? "") : (values.value ?? "");

    // Resolve underlying DOM element (AntD TextArea exposes resizableTextArea.textArea)
    const domEl = resolveDomElement(targetInputRef);

    if (
      domEl &&
      typeof domEl.selectionStart === "number" &&
      typeof domEl.selectionEnd === "number"
    ) {
      const start = domEl.selectionStart ?? currentValue.length;
      const end = domEl.selectionEnd ?? start;
      const nextValue = `${currentValue.slice(0, start)}${injection}${currentValue.slice(end)}`;
      setFieldValue(dataPull ? "path" : "value", nextValue);
      // Optional: reposition cursor just after inserted token
      requestAnimationFrame(() => {
        try {
          domEl.focus();
          const newPos = start + injection.length;
          domEl.setSelectionRange(newPos, newPos);
        } catch (_) {
          /* ignore */
        }
      });
    } else {
      // Fallback: append to end
      setFieldValue(dataPull ? "path" : "value", `${currentValue}${injection}`);
    }
  }, [
    dataPull,
    values.value,
    values.path,
    setFieldValue,
    targetInputRef,
    period,
    quantity,
    frequency,
    dateTimeFormat,
  ]);

  return (
    <div className="mt-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-[20%] min-w-[100px] shrink-0">
          <Select
            size="sm"
            aria-label="Period"
            value={period}
            defaultSelectedKeys={["last"]}
            onChange={updatePeriod}
          >
            <SelectItem key="last">{t("translation:last")}</SelectItem>
            <SelectItem key="next">{t("translation:next")}</SelectItem>
          </Select>
        </div>
        <div className="w-[12%] min-w-[70px] shrink-0">
          <Input
            type="number"
            min={1}
            max={100}
            value={String(quantity)}
            size="sm"
            aria-label="Quantity"
            onChange={updateQuantity}
          />
        </div>
        <div className="w-[25%] min-w-[120px] shrink-0">
          <Select
            size="sm"
            aria-label="Frequency"
            value={frequency}
            defaultSelectedKeys={["day"]}
            onChange={updateFrequency}
          >
            <SelectItem key="second">Second(s)</SelectItem>
            <SelectItem key="minute">Minute(s)</SelectItem>
            <SelectItem key="hour">Hour(s)</SelectItem>
            <SelectItem key="day">Day(s)</SelectItem>
            <SelectItem key="week">Week(s)</SelectItem>
            <SelectItem key="month">Month(s)</SelectItem>
          </Select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <Select
            size="sm"
            aria-label="Date format"
            value={dateTimeFormat}
            defaultSelectedKeys={["%Y%m%d.0"]}
            onChange={updateDateTimeFormat}
          >
            <SelectItem key="%Y%m%d.0">YYYYMMDD.0</SelectItem>
            <SelectItem key="%m/%d/%Y">MM/DD/YYYY</SelectItem>
            <SelectItem key="%d/%m/%Y">DD/MM/YYYY</SelectItem>
            <SelectItem key="%Y-%m-%d">YYYY-MM-DD</SelectItem>
            <SelectItem key="%Y-%m-%d %H:%M:%S">YYYY-MM-DD HH:MM:SS</SelectItem>
            <SelectItem key="%Y-%m-%d%H:%M:%S.%f">
              YYYY-MM-DDTHH:MM:SS.SSS
            </SelectItem>
            <SelectItem key="%Y-%m-%dT%H:%M:%SZ">
              YYYY-MM-DDTHH:MM:SSZ
            </SelectItem>
            <SelectItem key="%Y-%m-%d %H:%M:%S.%fZ">
              YYYY-MM-DD HH:MM:SS.SSSZ
            </SelectItem>
            <SelectItem key="%Y-%m-%dT%H:%M:%S%z">
              YYYY-MM-DDTHH:MM:SS+HH:MM
            </SelectItem>
            <SelectItem key="epoch">Epoch (seconds)</SelectItem>
            <SelectItem key="epoch_ms">Epoch (milliseconds)</SelectItem>
          </Select>
        </div>
      </div>
      <div className="mt-2 w-1/4 min-w-[100px]">
        <Button
          radius="none"
          isDisabled={disabled}
          onPress={addField}
          size="md"
          fullWidth
        >
          {t("translation:add")}
        </Button>
      </div>
    </div>
  );
}
